import React from "react";
import {
  Card,
  CardContent,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Button,
  Box,
  Grid,
  TextField,
  Tooltip,
  IconButton,
} from "@mui/material";
import Label from "../../../../components/Label/Label";
import RuleSelector from "./RuleSelector";
import "./ControlChartConfigs.scss";
import {
  limitTypeOptions,
  ruleOptions,
  // rulesetOptions,
  specLimitTypeOptions,
  targetLimitTypeOptions,
} from "../constants";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

const ControlChartConfigs = ({
  unitOfOperationId,
  updateState,
  handleGenerate,
  configState,
  onClickClearCurrentConfig,
  configSetIds,
  disableAllFields,
  setShowConfigPanel,
  setSelectedTab,
}) => {
  const onChangeSpecLimit = (e) => {
    const value = e.target.value;
    updateState("specLimitType", value);
    if (value !== "User Defined") {
      updateState("lowerSpecLimit", "");
      updateState("upperSpecLimit", "");
      updateState("specificationTarget", "");
    }
    if (value !== "User Preset") {
      updateState("specLimitSetId", "");
    }
  };

  const onChangeControlLimit = (e) => {
    const value = e.target.value;
    updateState("controlLimitType", value);
    if (value !== "User Defined") {
      updateState("lowerControlLimit", "");
      updateState("upperControlLimit", "");
    }
    if (value !== "User Preset") {
      updateState("controlLimitSetId", "");
    }
  };

  const onChangeTargetLimit = (e) => {
    const value = e.target.value;
    updateState("targetLimitType", value);
    if (value !== "User Defined") {
      updateState("targetLimit", "");
    }
  };

  const onChangeZoomScale = (e) => {
    const value = e.target.checked;
    updateState("showZoomScale", value);
    if (value === false) {
      updateState("zoomYMin", "");
      updateState("zoomYMax", "");
    }
  };

  // 🧠 Enhanced Validation
  const isApplyDisabled = () => {
    // Spec: User Preset → must select set
    const isSpecPresetInvalid =
      configState.specLimitType === "User Preset" &&
      !configState.specLimitSetId;

    // Spec: User Defined → must enter at least one (lower/upper/target)
    // const isSpecDefinedInvalid =
    //   configState.specLimitType === "User Defined" &&
    //   !(
    //     (configState.lowerSpecLimit && configState.upperSpecLimit)
    //     // ||
    //     // configState.specificationTarget
    //   );

    // Control: User Preset → must select set
    const isControlPresetInvalid =
      configState.controlLimitType === "User Preset" &&
      !configState.controlLimitSetId;

    // Control: User Defined → must enter at least one (lower/upper)
    const isControlDefinedInvalid =
      configState.controlLimitType === "User Defined" &&
      !(configState.lowerControlLimit && configState.upperControlLimit);

    return (
      !unitOfOperationId ||
      isSpecPresetInvalid ||
      // isSpecDefinedInvalid ||
      isControlPresetInvalid ||
      isControlDefinedInvalid
    );
  };

  return (
    <Box className="plot-config-wrapper">
      <Box className="scrollable-config">
        {/* Specification Limit */}
        <Card elevation={0} className="filter-card plot-config-card">
          <CardContent className="toggle-section">
            <Label text="Specification Limit" fontWeight="500" />

            <FormControl
              fullWidth
              size="small"
              className="dropdown-field"
              sx={{ mt: 1 }}
              disabled={disableAllFields}
              error={
                configState.specLimitType === "User Preset" &&
                !configState.specLimitSetId
              }
            >
              <InputLabel>Select Limit</InputLabel>
              <Select
                value={configState.specLimitType}
                onChange={(e) => onChangeSpecLimit(e)}
                // updateState("specLimitType", e.target.value)}
                label="Select Limit"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {specLimitTypeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {configState.specLimitType === "User Preset" && (
              <FormControl
                fullWidth
                size="small"
                sx={{ mt: 1 }}
                disabled={disableAllFields}
                error={!configState.specLimitSetId}
              >
                <InputLabel id="spec-site-label">Select Set</InputLabel>
                <Select
                  labelId="spec-site-label"
                  label="Select Set"
                  value={configState.specLimitSetId || ""}
                  onChange={(e) =>
                    updateState("specLimitSetId", e.target.value)
                  }
                >
                  {Array.isArray(configSetIds?.specification_limit_options) &&
                  configSetIds?.specification_limit_options.length > 0 ? (
                    configSetIds.specification_limit_options.map((site) => (
                      <MenuItem
                        key={site?.spec_limit_id}
                        value={site?.spec_limit_id}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                          }}
                        >
                          <Label text={site?.identifier} />
                          <Tooltip
                            arrow
                            placement="right"
                            title={
                              <Box
                                sx={{
                                  borderRadius: 2,
                                  boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                                  p: 2,
                                  backgroundColor: "#fff",
                                  minWidth: 220,
                                }}
                              >
                                <Label
                                  text="Specification Limits"
                                  fontWeight="600"
                                  fontSize="14px"
                                  color="#1976d2"
                                />
                                <Divider sx={{ my: 1 }} />
                                <Box
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: "80px 1fr",
                                    rowGap: 0.5,
                                  }}
                                >
                                  <Label text="LSL:" fontWeight="500" />
                                  <Label text={site.lower_spec ?? "N/A"} />
                                  <Label text="USL:" fontWeight="500" />
                                  <Label text={site.upper_spec ?? "N/A"} />
                                  <Label text="Target:" fontWeight="500" />
                                  <Label text={site.process_target ?? "N/A"} />
                                </Box>
                              </Box>
                            }
                          >
                            <IconButton
                              size="small"
                              sx={{
                                p: 0.5,
                                color: "#1976d2",
                                "&:hover": {
                                  color: "#0d47a1",
                                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                                },
                              }}
                            >
                              <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      <Label text="No sets available" />
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            )}

            {configState.specLimitType === "User Defined" && (
              <Grid container spacing={2} pt={1}>
                <Grid item xs={6}>
                  <TextField
                    type="number"
                    label="Lower Spec Limit"
                    size="small"
                    fullWidth
                    value={configState.lowerSpecLimit}
                    onChange={(e) =>
                      updateState("lowerSpecLimit", e.target.value)
                    }
                    disabled={disableAllFields}
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    type="number"
                    label="Upper Spec Limit"
                    size="small"
                    fullWidth
                    value={configState.upperSpecLimit}
                    onChange={(e) =>
                      updateState("upperSpecLimit", e.target.value)
                    }
                    disabled={disableAllFields}
                    required
                  />
                </Grid>
                <Grid item xs={12} className="padding-top-8">
                  <TextField
                    type="number"
                    label="Target"
                    size="small"
                    fullWidth
                    value={configState.specificationTarget}
                    onChange={(e) =>
                      updateState("specificationTarget", e.target.value)
                    }
                    disabled={disableAllFields}
                  />
                </Grid>

                {/* {configState.specLimitType === "User Defined" &&
                  !(
                    (configState.lowerSpecLimit && configState.upperSpecLimit)
                    //    ||
                    // configState.specificationTarget
                  ) && (
                    <Grid item xs={12} sx={{ paddingTop: "8px !important" }}>
                      <Box
                        sx={{
                          color: "error.main",
                          ml: "8px",
                        }}
                      >
                        <Label
                          fontSize="12px"
                          color="inherit"
                          text="Enter the lower and upper spec limit values."
                        />
                      </Box>
                    </Grid>
                  )} */}
              </Grid>
            )}
            {/* Control Limit */}
            <Box sx={{ mt: 1 }}>
              <Label text="Control Limit" fontWeight="500" />
              <FormControl
                fullWidth
                size="small"
                className="dropdown-field"
                sx={{ mt: 1 }}
                disabled={disableAllFields}
                error={
                  configState.controlLimitType === "User Preset" &&
                  !configState.controlLimitSetId
                }
              >
                <InputLabel>Select Limit</InputLabel>
                <Select
                  value={configState.controlLimitType}
                  onChange={(e) => onChangeControlLimit(e)}
                  label="Select Limit"
                >
                  {limitTypeOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {configState.controlLimitType === "User Preset" && (
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                  disabled={disableAllFields}
                  error={!configState.controlLimitSetId}
                >
                  <InputLabel id="control-site-label">Select Set</InputLabel>
                  <Select
                    labelId="control-site-label"
                    value={configState.controlLimitSetId || ""}
                    onChange={(e) =>
                      updateState("controlLimitSetId", e.target.value)
                    }
                    label="Select Set"
                  >
                    {Array.isArray(configSetIds?.control_limit_options) &&
                    configSetIds?.control_limit_options.length > 0 ? (
                      configSetIds.control_limit_options.map((site) => (
                        <MenuItem
                          key={site?.stat_limit_id}
                          value={site?.stat_limit_id}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              width: "100%",
                            }}
                          >
                            <Label text={site?.set_id} />
                            <Tooltip
                              arrow
                              placement="right"
                              title={
                                <Box
                                  sx={{
                                    borderRadius: 2,
                                    boxShadow:
                                      "0 4px 10px rgba(182, 180, 180, 0.15)",
                                    p: 2,
                                    backgroundColor: "#fff",
                                    minWidth: 220,
                                  }}
                                >
                                  <Label
                                    text="Control Limits"
                                    fontWeight="600"
                                    fontSize="14px"
                                    color="#1976d2"
                                  />
                                  <Divider sx={{ my: 1 }} />
                                  <Box
                                    sx={{
                                      display: "grid",
                                      gridTemplateColumns: "100px 1fr",
                                      rowGap: 0.5,
                                    }}
                                  >
                                    <Label text="UCL:" fontWeight="500" />
                                    <Label text={site.upper_control ?? "N/A"} />
                                    <Label text="LCL:" fontWeight="500" />
                                    <Label text={site.lower_control ?? "N/A"} />
                                  </Box>
                                </Box>
                              }
                            >
                              <IconButton
                                size="small"
                                sx={{
                                  p: 0.5,
                                  color: "#1976d2",
                                  "&:hover": {
                                    color: "#0d47a1",
                                    backgroundColor: "rgba(25, 118, 210, 0.08)",
                                  },
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <InfoOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>
                        <Label text="No sets available" />
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              )}

              {configState.controlLimitType === "User Defined" && (
                <Grid container spacing={0.5} pt={1} alignItems="center">
                  <Grid item xs={6}>
                    <TextField
                      type="number"
                      label="Lower Limit"
                      size="small"
                      fullWidth
                      value={configState.lowerControlLimit}
                      onChange={(e) =>
                        updateState("lowerControlLimit", e.target.value)
                      }
                      disabled={disableAllFields}
                      required
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      type="number"
                      label="Upper Limit"
                      size="small"
                      fullWidth
                      value={configState.upperControlLimit}
                      onChange={(e) =>
                        updateState("upperControlLimit", e.target.value)
                      }
                      disabled={disableAllFields}
                      required
                    />
                  </Grid>

                  {configState.controlLimitType === "User Defined" &&
                    !(
                      configState.lowerControlLimit &&
                      configState.upperControlLimit
                    ) && (
                      <Grid item xs={12} sx={{ paddingTop: "8px !important" }}>
                        <Box
                          sx={{
                            color: "error.main",
                            ml: "8px",
                          }}
                        >
                          <Label
                            fontSize="12px"
                            color="inherit"
                            text="Enter the lower and upper control limit values."
                          />
                        </Box>
                      </Grid>
                    )}
                </Grid>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Zoom Scale */}
        <Card elevation={0} className="filter-card plot-config-card">
          <CardContent>
            <FormControlLabel
              sx={{ pl: "8px", pr: "8px" }}
              control={
                <Switch
                  checked={configState.showZoomScale}
                  onChange={(e) => onChangeZoomScale(e)}
                />
              }
              label="Scale"
              disabled={disableAllFields}
            />

            {configState.showZoomScale && (
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <TextField
                    type="number"
                    label="Y Min"
                    size="small"
                    fullWidth
                    value={configState.zoomYMin || ""}
                    onChange={(e) => updateState("zoomYMin", e.target.value)}
                    disabled={disableAllFields}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    type="number"
                    label="Y Max"
                    size="small"
                    fullWidth
                    value={configState.zoomYMax || ""}
                    onChange={(e) => updateState("zoomYMax", e.target.value)}
                    disabled={disableAllFields}
                  />
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>

        {/* Toggles */}
        <Card elevation={0} className="filter-card plot-config-card">
          <CardContent className="toggle-section">
            <FormControlLabel
              sx={{ pl: "8px", pr: "8px" }}
              control={
                <Switch
                  checked={configState.movingAverage}
                  onChange={(e) =>
                    updateState("movingAverage", e.target.checked)
                  }
                />
              }
              label={<Label text="Moving Range" fontWeight="500" />}
              disabled={disableAllFields}
            />

            <Divider className="plot-section-divider" />

            <FormControlLabel
              sx={{ pl: "8px", pr: "8px" }}
              control={
                <Switch
                  checked={configState.plotAggregation}
                  onChange={(e) =>
                    updateState("plotAggregation", e.target.checked)
                  }
                />
              }
              label={<Label text="Plot Data Aggregation" fontWeight="500" />}
              disabled={disableAllFields}
            />
             <Divider className="plot-section-divider" />
            <FormControlLabel
              sx={{ pl: "8px", pr: "8px" }}
              control={
                <Switch
                  checked={configState.show_target}
                  onChange={(e) =>
                    updateState("show_target", e.target.checked)
                  }
                />
              }
              label={<Label text="Show Target" fontWeight="500" />}
              disabled={disableAllFields}
            />
             <Divider className="plot-section-divider" />
            <FormControlLabel
              sx={{ pl: "8px", pr: "8px" }}
              control={
                <Switch
                  checked={configState.show_grid}
                  onChange={(e) =>
                    updateState("show_grid", e.target.checked)
                  }
                />
              }
              label={<Label text="Show Grid" fontWeight="500" />}
              disabled={disableAllFields}
            />
          </CardContent>
        </Card>

        {/* Nelson Rules */}
        <Card elevation={0} className="filter-card plot-config-card">
          <CardContent className="rule-section">
            <Label text="Nelson Rules" fontWeight="500" />
            <RuleSelector
              disabled={disableAllFields}
              ruleOptions={ruleOptions}
              selectedRules={configState.selectedRules}
              setSelectedRules={(val) => updateState("selectedRules", val)}
            />
          </CardContent>
        </Card>
      </Box>

      {/* Sticky Footer */}
      <Box className="plot-footer-sticky">
        <Card elevation={0} className="filter-card plot-footer-btn-card">
          <CardContent>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              {/* LEFT — Previous */}
              <Box>
                <Tooltip title="Previous">
                  <IconButton
                    onClick={() => setSelectedTab("batchFilters")}
                    size="small"
                    sx={{
                      backgroundColor: "primary.main",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "primary.dark",
                      },
                      borderRadius: "8px",
                      px: 1,
                      py: 0.6,
                    }}
                  >
                    <ArrowBackIosNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* RIGHT — Other Buttons */}
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  className="close-btn"
                  onClick={() => setShowConfigPanel(false)}
                >
                  Close
                </Button>

                <Button
                  variant="outlined"
                  onClick={onClickClearCurrentConfig}
                  disabled={disableAllFields}
                >
                  Clear Config
                </Button>

                <Button
                  variant="contained"
                  onClick={handleGenerate}
                  disabled={isApplyDisabled() || disableAllFields}
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default ControlChartConfigs;
